
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Building, Settings, Mail, UserPlus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type OrgMember = {
  id: string;
  user_id: string;
  role: string;
  full_name: string;
  email: string;
  is_active: boolean;
};

const OrganizationProfile = () => {
  const { user, profile, organization, refreshProfile } = useAuth();
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const navigate = useNavigate();

  useEffect(() => {
    if (organization) {
      loadOrganizationMembers();
    } else {
      setLoading(false);
    }
  }, [organization]);

  const loadOrganizationMembers = async () => {
    if (!organization) return;
    
    try {
      setLoading(true);
      
      // Récupérer tous les membres de l'organisation
      const { data: membersData, error: membersError } = await supabase
        .from('organization_members')
        .select('id, user_id, role')
        .eq('organization_id', organization.id);
      
      if (membersError) throw membersError;
      
      // Récupérer les profils complets pour chaque membre
      const membersWithProfiles = await Promise.all(
        (membersData || []).map(async (member) => {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('full_name, email, is_active')
            .eq('id', member.user_id)
            .single();
          
          if (profileError) {
            console.error('Erreur lors de la récupération du profil:', profileError);
            return {
              ...member,
              full_name: 'Inconnu',
              email: 'email@inconnu.com',
              is_active: false
            };
          }
          
          return {
            ...member,
            full_name: profile.full_name || 'Sans nom',
            email: profile.email,
            is_active: profile.is_active
          };
        })
      );
      
      setMembers(membersWithProfiles);
    } catch (error) {
      console.error('Erreur lors du chargement des membres:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les membres de l'organisation.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!organization) return;
    
    try {
      // Vérifier si l'utilisateur existe déjà
      const { data: existingUser, error: existingUserError } = await supabase
        .from('profiles')
        .select('id, email')
        .eq('email', inviteEmail)
        .maybeSingle();
      
      if (existingUserError) throw existingUserError;
      
      if (!existingUser) {
        toast({
          title: "Information",
          description: "Cet utilisateur n'existe pas encore. Il sera invité à rejoindre la plateforme."
        });
        
        // Ici, vous pourriez ajouter un code pour envoyer une invitation par email
        setIsInviteOpen(false);
        setInviteEmail('');
        setInviteRole('member');
        return;
      }
      
      // Vérifier si l'utilisateur est déjà membre
      const { data: existingMember, error: memberCheckError } = await supabase
        .from('organization_members')
        .select('id')
        .eq('organization_id', organization.id)
        .eq('user_id', existingUser.id)
        .maybeSingle();
      
      if (memberCheckError) throw memberCheckError;
      
      if (existingMember) {
        toast({
          title: "Information",
          description: "Cet utilisateur est déjà membre de l'organisation."
        });
        setIsInviteOpen(false);
        setInviteEmail('');
        setInviteRole('member');
        return;
      }
      
      // Ajouter l'utilisateur comme membre
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: organization.id,
          user_id: existingUser.id,
          role: inviteRole
        });
      
      if (memberError) throw memberError;
      
      // Mettre à jour le corporate_group_id dans le profil de l'utilisateur
      const { error: updateProfileError } = await supabase
        .from('profiles')
        .update({ corporate_group_id: organization.id })
        .eq('id', existingUser.id);
      
      if (updateProfileError) throw updateProfileError;
      
      // Actualiser la liste des membres
      await loadOrganizationMembers();
      
      toast({
        title: "Utilisateur ajouté",
        description: `${inviteEmail} a été ajouté à l'organisation.`
      });
      
      setIsInviteOpen(false);
      setInviteEmail('');
      setInviteRole('member');
    } catch (error: any) {
      console.error('Erreur lors de l\'invitation:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'inviter l'utilisateur.",
        variant: "destructive"
      });
    }
  };

  const handleRemoveMember = async (memberId: string, userId: string) => {
    if (!organization) return;
    
    try {
      // Supprimer le membre de l'organisation
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('id', memberId);
      
      if (error) throw error;
      
      // Mettre à jour le profil pour enlever l'association à l'organisation
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ corporate_group_id: null })
        .eq('id', userId)
        .eq('corporate_group_id', organization.id);
      
      if (updateError) throw updateError;
      
      // Actualiser la liste des membres
      await loadOrganizationMembers();
      
      toast({
        title: "Membre supprimé",
        description: "Le membre a été retiré de l'organisation."
      });
    } catch (error: any) {
      console.error('Erreur lors de la suppression du membre:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de supprimer le membre.",
        variant: "destructive"
      });
    }
  };

  const isOwner = organization?.owner_id === user?.id;
  const userRole = members.find(m => m.user_id === user?.id)?.role || 'member';
  const canManageMembers = isOwner || userRole === 'admin';

  if (!profile) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardHeader>
            <CardTitle>Profil d'Organisation</CardTitle>
            <CardDescription>
              Vous n'êtes pas membre d'une organisation.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center py-8">
            <Building className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="mb-4">Vous n'êtes pas actuellement associé à une organisation.</p>
            <Button onClick={() => navigate('/dashboard')}>
              Retourner au tableau de bord
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  <span className="flex items-center">
                    <Building className="mr-2 h-6 w-6" />
                    {organization.name}
                  </span>
                </CardTitle>
                <CardDescription>
                  Profil de votre organisation
                </CardDescription>
              </div>
              {isOwner && (
                <Button variant="outline" size="sm" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Gérer l'organisation
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-muted-foreground">Plan</Label>
                <p className="font-medium">{organization.plan}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Statut</Label>
                <p className="font-medium">
                  <Badge className={organization.is_active ? "bg-green-500" : "bg-red-500"}>
                    {organization.is_active ? "Actif" : "Inactif"}
                  </Badge>
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">Propriétaire</Label>
                <p className="font-medium">{isOwner ? `${profile.full_name} (Vous)` : 'Autre utilisateur'}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Votre rôle</Label>
                <p className="font-medium capitalize">{isOwner ? 'Propriétaire' : userRole}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  <span className="flex items-center">
                    <Users className="mr-2 h-6 w-6" />
                    Membres de l'organisation
                  </span>
                </CardTitle>
                <CardDescription>
                  Liste des utilisateurs dans votre organisation
                </CardDescription>
              </div>
              {canManageMembers && (
                <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
                  <DialogTrigger asChild>
                    <Button className="flex items-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      <span>Inviter un membre</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Inviter un nouveau membre</DialogTitle>
                      <DialogDescription>
                        Invitez un utilisateur à rejoindre votre organisation.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                      <div>
                        <Label htmlFor="invite-email">Email</Label>
                        <Input 
                          id="invite-email" 
                          type="email" 
                          placeholder="email@exemple.com" 
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="invite-role">Rôle</Label>
                        <Select value={inviteRole} onValueChange={setInviteRole}>
                          <SelectTrigger id="invite-role">
                            <SelectValue placeholder="Sélectionner un rôle" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Administrateur</SelectItem>
                            <SelectItem value="member">Membre</SelectItem>
                            <SelectItem value="evaluator">Évaluateur</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsInviteOpen(false)}>Annuler</Button>
                      <Button onClick={handleInviteUser}>Inviter</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead>Statut</TableHead>
                      {canManageMembers && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={canManageMembers ? 5 : 4} className="text-center py-8 text-gray-500">
                          Aucun membre trouvé
                        </TableCell>
                      </TableRow>
                    ) : (
                      members.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell className="font-medium">
                            {member.full_name}
                            {member.user_id === user?.id && <span className="ml-2 text-xs text-gray-500">(Vous)</span>}
                          </TableCell>
                          <TableCell>{member.email}</TableCell>
                          <TableCell className="capitalize">{member.role}</TableCell>
                          <TableCell>
                            <Badge className={member.is_active ? "bg-green-500" : "bg-gray-500"}>
                              {member.is_active ? "Actif" : "Inactif"}
                            </Badge>
                          </TableCell>
                          {canManageMembers && (
                            <TableCell className="text-right">
                              {member.user_id !== user?.id && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => handleRemoveMember(member.id, member.user_id)}
                                >
                                  Retirer
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrganizationProfile;
